import { useCallback, useState, useRef, useEffect } from 'react';
import { TableTabNavCombinedProps, TableTabNavStateEnum, TabNavPositions, BtnPositionEnum } from './types';
import { SCROLLABLE_PACE } from '../../../lib/constants';
import { useResizeObserver } from '../../../lib/hooks/useResizeObserver';
import styles from './TableTabNav.module.scss';

const usePresenter = (props: TableTabNavCombinedProps): TableTabNavCombinedProps => {
  const { isLoading, tableTabList, tabsAutoScrollEnabled, setTabsAutoScrollEnabled } = props;

  const tabListRef = useRef<HTMLDivElement>(null);

  const [leftButtonHovered, setLeftButtonHovered] = useState(false);
  const [rightButtonHovered, setRightButtonHovered] = useState(false);
  const [tableOverflow, setTableOverflow] = useState<TableTabNavStateEnum>('NoTableOverflow');
  const [activeTabNode, setActiveTabNode] = useState<HTMLButtonElement>();
  const [currentTableListWidth, setCurrentTableListWidth] = useState(0);
  const [tabNavPositions, setTabNavPositions] = useState<TabNavPositions>({ 
    totalLength: 0,
    step: 0,
    current: 0,
    totalOverflow: 0,
  });

  // after the page is rendered there is a glitch, which affects the size of tabList container
  // this glitch doesn't allow to calculate all relative values correctly. Thus, the resizeObserver
  // was used to watch after the tableList wrapper, it gets an actual up-to-date value of it's width.
  // this helps to know its exact width on the initial load of the page.
  const tableListRefWidth = useResizeObserver(tabListRef);

  useEffect(() => {
    const { current, totalOverflow } = tabNavPositions;
    if (activeTabNode && tabListRef?.current && tableListRefWidth) {
      const tabNavVisiblePart = tabListRef?.current?.offsetWidth;
      const positionToScroll = Number(activeTabNode.offsetLeft) - tabListRef?.current?.offsetLeft;

      if (tabsAutoScrollEnabled || tableListRefWidth !== currentTableListWidth) {
        tabListRef?.current?.scrollTo({
          left: positionToScroll + activeTabNode.offsetWidth > tabNavVisiblePart ? positionToScroll : 0, // no need for a scroll if it's within visible space
        });

        setCurrentTableListWidth(tableListRefWidth);
  
        // scroll to the very top, if the menu (not a bar) with tabs is too long
        const header = tabListRef?.current?.parentNode?.parentNode?.parentNode as HTMLElement;
        header.scrollIntoView({ behavior: 'smooth' });
  
        const currentScrollPosition = tabListRef?.current?.scrollLeft;
        const valueToScroll = currentScrollPosition >= 0 ? tabListRef?.current?.scrollLeft : 0;
        const currentTotalOverflow = tabListRef?.current?.scrollWidth - tabListRef?.current?.clientWidth;
  
        // jumps if tables in scrollable area;
        setTabNavPositions({
          ...tabNavPositions,
          current: currentTotalOverflow < current ? current : valueToScroll,
          totalOverflow: currentTotalOverflow >= totalOverflow ? currentTotalOverflow : totalOverflow,
        });
        if (setTabsAutoScrollEnabled) {
          setTabsAutoScrollEnabled(false);
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, !tableTabList?.tableTabItems, activeTabNode, tabListRef?.current, tableListRefWidth]);

  const tabWrapper = useCallback((node: HTMLDivElement) => {
    if (node) {
      const tabList = Array.from(node.childNodes).find(childNode => childNode.nodeName === 'DIV') as HTMLDivElement;
      const hasOverflow = tabList.scrollWidth > tabList.clientWidth;
      const isTableOverflow = hasOverflow ? 'TableOverflow' : 'NoTableOverflow';
      setTableOverflow(isTableOverflow);
      const totalOverflow = tabList.scrollWidth - tabList.clientWidth;
      // get a tenth of visible content (10% out of 100%), if it's bigger than overflow - set clientWidth
      // as max possible scroll position:
      const step = tabList.clientWidth / SCROLLABLE_PACE < totalOverflow ? (
        tabList.clientWidth / 10
      ) : (
        tabList.clientWidth
      ); 
      setTabNavPositions({
        ...tabNavPositions,
        totalLength: tabList.scrollWidth,
        step,
        totalOverflow,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, !tableTabList?.tableTabItems]);

  const activeTableTabRef = useCallback((node: HTMLButtonElement) => {
    if (node && !isLoading && tableOverflow === 'TableOverflow') {
      setActiveTabNode(node);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, !tableTabList?.tableTabItems?.length, tableOverflow]);

  const handleRightButtonClick = () => {
    if (tabListRef?.current) {
      const { step, current, totalOverflow } = tabNavPositions;
      tabListRef?.current?.scroll({ 
        left: current + step,
        behavior: 'smooth',
      });
      const currentTotalOverflow = tabListRef?.current?.scrollWidth - tabListRef?.current?.clientWidth;
      if (totalOverflow !== currentTotalOverflow) {
        // during the update of the overflow state of the bar the params are changing
        // values which are to be updated are only affect the extreme right position
        // it cannot be done in useEffect because layout changes doesn't get updated 'in time'
        const adjustedTotalLength = tabListRef?.current?.scrollWidth;
        const adjustedTotalOverflow = tabListRef?.current?.scrollWidth - tabListRef?.current?.clientWidth;

        setTabNavPositions({
          ...tabNavPositions,
          totalLength: adjustedTotalLength,
          totalOverflow: adjustedTotalOverflow,
          current: totalOverflow <= current ? current : tabListRef?.current?.scrollLeft + step,
        });
      } else {
        setTabNavPositions({
          ...tabNavPositions,
          current: totalOverflow <= current ? current : tabListRef?.current?.scrollLeft + step,
        });
      }
    }
  };

  const handleLeftButtonClick = () => {
    if (tabListRef?.current) {
      const { step, current } = tabNavPositions;
      tabListRef?.current?.scroll({ 
        left: current - step,
        behavior: 'smooth',
      });
      setTabNavPositions({
        ...tabNavPositions,
        current: current - step >= 0 ? current - step : 0,
      });
    }
  };

  const handleButtonMouseEnter = (btn: BtnPositionEnum) => {
    if (btn === 'Left') {
      setLeftButtonHovered(true);
    } else {
      setRightButtonHovered(true);
    }
  };
  const handleButtonMouseLeave = (btn: BtnPositionEnum) => {
    if (btn === 'Left') {
      setLeftButtonHovered(false);
    } else {
      setRightButtonHovered(false);
    }
  };

  const getLeftIconColour = () => {
    if (tabNavPositions.current === 0) {
      return 'IconSubdued';
    } else if (leftButtonHovered) {
      return 'IconPrimary';
    } else {
      return 'IconDark';
    }
  };

  const getRightIconColour = () => {
    const { current, totalOverflow } = tabNavPositions;

    if (totalOverflow <= current) {
      return 'IconSubdued';
    } else if (rightButtonHovered) {
      return 'IconPrimary';
    } else {
      return 'IconDark';
    }
  };

  const getMappedTableTabItems = useCallback(() => {
    if (Array.isArray(tableTabList?.tableTabItems)) {
      return tableTabList?.tableTabItems.map((tabItem) => {
        const isTableTabNeedsScroll = tabItem.state === 'Selected' && tableOverflow === 'TableOverflow';
        return {
          ...tabItem,
          tabIndex: -1,
          activeTableRef: isTableTabNeedsScroll ? activeTableTabRef : undefined,
        };
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, tableTabList?.tableTabItems, tableOverflow]);

  return {
    ...props,
    tableTabListWrapperRef: tabWrapper,
    tableTabList: {
      ...props.tableTabList,
      tableTabItems: getMappedTableTabItems(),
      classes: {
        tableTabItem: styles.tableTabItem,
      },
      tableTabListRef: tabListRef,
    },
    state: tableOverflow,
    tableLeftButton: {
      icon: {
        asset: 'ChevronLeft',
        colour: getLeftIconColour(),
      },
      onClick: handleLeftButtonClick,
      onMouseEnter: () => handleButtonMouseEnter('Left'),
      onMouseLeave: () => handleButtonMouseLeave('Left'),
      disabled: tabNavPositions.current === 0,
      tabIndex: tabNavPositions.current === 0 ? -1 : 0,
    },
    tableRightButton: {
      icon: {
        asset: 'ChevronRight',
        colour: getRightIconColour(),
      },
      onClick: handleRightButtonClick,
      onMouseEnter: () => handleButtonMouseEnter('Right'),
      onMouseLeave: () => handleButtonMouseLeave('Right'),
      disabled: tabNavPositions.totalOverflow <= tabNavPositions.current,
      tabIndex: tabNavPositions.totalOverflow <= tabNavPositions.current ? -1 : 0,
    },
  };
};

export default usePresenter;
